import { ChevronDown, ChevronUp, X } from "lucide-react";
import { LucideIcon } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

type CustomSelectProps = {
  options: string[];
  selectedOption?: string;
  onSelect: (option: string | undefined) => void;
  placeholder?: string;
  icon?: LucideIcon;
  width?: string | number;
  fontWeight?: number | string;
  fontSize?: string;
  deselectable?: boolean;
};

const CustomSelect = ({
  options,
  selectedOption,
  onSelect,
  placeholder = "Escoge una opción",
  icon,
  width,
  fontWeight = 400,
  fontSize,
  deselectable = true,
}: CustomSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSelectedOption, setInternalSelectedOption] = useState<string | undefined>(
    selectedOption
  );
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInternalSelectedOption(selectedOption);
  }, [selectedOption]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleSelect = (option: string) => {
    setInternalSelectedOption(option);
    onSelect(option);
    setIsOpen(false);
  };

  const handleDeselect = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInternalSelectedOption(undefined);
    onSelect(undefined);
  };

  const IconComponent = icon || (isOpen ? ChevronUp : ChevronDown);

  return (
    <div
      className="relative"
      style={{ width }}
      ref={selectRef}
      onKeyDown={handleKeyDown}
    >
      {/* Selector Box */}
      <div
        className="flex w-full cursor-pointer items-center justify-between rounded-md border border-gray-primary bg-white px-3 py-1.5 hover:bg-gray-100"
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        role="combobox"
      >
        <span
          className={cn(
            internalSelectedOption ? "text-palomma-secondary" : "text-gray-secondary"
          )}
          style={{ fontWeight, fontSize }}
        >
          {internalSelectedOption || placeholder}
        </span>
        <div className="flex items-center gap-2">
          {internalSelectedOption && deselectable && (
            <X className="size-4 cursor-pointer text-gray-500" onClick={handleDeselect} />
          )}
          <IconComponent className="size-4 text-gray-secondary" />
        </div>
      </div>

      {/* Dropdown List */}
      {isOpen && (
        <div
          className="absolute left-0 top-full z-10 mt-2 max-h-[200px] w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
          role="listbox"
        >
          <div className="flex flex-col">
            {options.map((option) => (
              <div
                key={option}
                className="cursor-pointer p-3 hover:bg-gray-100"
                onClick={() => handleSelect(option)}
                role="option"
                aria-selected={option === internalSelectedOption}
              >
                <span style={{ fontSize }}>{option}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
